import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import './desktop-category-product-list-template-style.scss';

import Typography from '@src/componentsNew/Typography/Typography';
import Button from '@src/componentsNew/Buttons/Button';
import DesktopProductListFilter from '@src/componentsNew/Filter/DesktopProductListFilter/DesktopProductListFilter';
import DesktopProductListSortingFilter from '@src/componentsNew/Filter/DesktopProductListFilter/DesktopProductListSortingFilter';
import DesktopBasicProductList from '@src/componentsNew/ProductList/Basic/DesktopBasicProductList';

import useIsComDomain from '@src/hooks/useIsComDomain';

import { productsVariables } from '@components/ProductPage/ProductList/__generated__/products';
import { StockAvailability, ProductOrderField, OrderDirection } from '__generated__/globalTypes';
import { getGraphqlIdFromDBId } from '@src/core/utils';
import { extractIdFromUrl } from '@src/utils';
import { goToUrl } from '@src/utils/urlCheck';
import { DESKTOP_PRODUCT_LIST_REQUEST_COUNT } from '@src/utils/constants';

type IProps = {
  selectedCategoryDBId?: number | null;
  isNotExistLocalData?: boolean;
  isLevelZero?: boolean;
  level0Category?: any;
  level1Category?: any;
  level2Category?: any;
  setLevel2Category?: any;
  setSelectedCategoryDBId?: React.Dispatch<React.SetStateAction<number | null>>;
};

const DesktopCategoryProductListTemplate = ({
  selectedCategoryDBId,
  isNotExistLocalData,
  isLevelZero,
  level0Category,
  level1Category,
  level2Category,
  setLevel2Category,
  setSelectedCategoryDBId,
}: IProps) => {
  const history = useHistory();
  const isComDomain = useIsComDomain();

  const initialVariables = {
    categories: [getGraphqlIdFromDBId(String(selectedCategoryDBId), 'Category')],
    stockAvailability: StockAvailability.IN_STOCK,
    sortBy: {
      field: ProductOrderField.POPULARITY,
      direction: OrderDirection.DESC,
    },
    first: DESKTOP_PRODUCT_LIST_REQUEST_COUNT,
    ...(isComDomain && { isMgs: true }),
  };

  const [variables, setVariables] = useState<productsVariables>(initialVariables);
  const [numOfProducts, setNumOfProducts] = useState<number>(0);

  useEffect(() => {
    setVariables((prev) => ({
      ...prev,
      categories: [getGraphqlIdFromDBId(String(selectedCategoryDBId), 'Category')],
    }));
  }, [selectedCategoryDBId]);

  // Fix after Desktop View UI completed
  const handleClickLevel1CategoryButton = useCallback(
    (e, categoryItem) => {
      e.persist();
      if (isLevelZero) {
        setSelectedCategoryDBId(categoryItem?.id);
        setLevel2Category(categoryItem?.child_items);
      } else {
        goToUrl(history, categoryItem?.url);
      }
    },
    [isLevelZero],
  );

  const handleClickLevel2CategoryButton = useCallback(
    (e, categoryItem, isAll: boolean = false) => {
      e.persist();

      if (isLevelZero) {
        if (isAll) {
          setSelectedCategoryDBId(categoryItem?.parentId);
        } else {
          const level2CategoryId = extractIdFromUrl(categoryItem?.url);

          setSelectedCategoryDBId(level2CategoryId);
        }
      } else {
        goToUrl(history, categoryItem?.url);
      }
    },
    [isLevelZero],
  );

  return (
    <div className="desktop-category-product-list-template-wrapper">
      <div className="category-product-list-top-wrapper">
        <div className="content-area">
          <div className="content-top">
            <div className="category-wrapper">
              <Typography variant="h6" component="h6">
                {level0Category?.name || '-'}
              </Typography>

              {!isNotExistLocalData && (
                <>
                  <Typography variant="h6" component="h6">
                    /
                  </Typography>
                  <Typography
                    cName="level1-category-name"
                    variant="h6"
                    component="h6"
                    onClick={(e) =>
                      handleClickLevel1CategoryButton(e, isLevelZero ? level1Category[0] : level1Category)
                    }
                  >
                    {isLevelZero ? level1Category[0]?.name : level1Category?.name}
                  </Typography>
                </>
              )}
            </div>
            {numOfProducts > 0 ? (
              <div className="sorting-filter-area">
                <Typography variant="body1" component="span" style={{ color: '#666666' }}>
                  {`${numOfProducts} Items`}
                </Typography>

                <DesktopProductListSortingFilter variables={variables} setVariables={setVariables} />
              </div>
            ) : null}
          </div>
          {level2Category?.length > 0 ? (
            <div className="content-bottom">
              <Button
                size="small"
                cName={`${
                  (
                    isLevelZero
                      ? selectedCategoryDBId === level2Category?.[0]?.parentId
                      : selectedCategoryDBId === level1Category?.id
                  )
                    ? 'selected'
                    : ''
                }`}
                onClick={(e) =>
                  handleClickLevel2CategoryButton(e, isLevelZero ? level2Category[0] : level1Category, true)
                }
              >
                All
              </Button>
              {level2Category?.map((level2) => (
                <Button
                  key={level2.url}
                  size="small"
                  cName={`${selectedCategoryDBId === extractIdFromUrl(level2.url) ? 'selected' : ''}`}
                  onClick={(e) => handleClickLevel2CategoryButton(e, level2)}
                >
                  {level2.name}
                </Button>
              ))}
            </div>
          ) : null}
        </div>
        <div className="divider" />
      </div>

      <div className={`main-area ${isNotExistLocalData ? 'no-navbar-category' : ''}`}>
        <div className="desktop-prodcut-list-filter-wrapper">
          <DesktopProductListFilter
            initialVariables={initialVariables}
            variables={variables}
            setVariables={setVariables}
          />
        </div>

        <div className={`products-area ${numOfProducts === 0 ? 'no-product' : ''}`}>
          <DesktopBasicProductList
            initialVariables={initialVariables}
            variables={variables}
            setNumOfProducts={setNumOfProducts}
          />
        </div>
      </div>
    </div>
  );
};

export default DesktopCategoryProductListTemplate;
